import styled from 'styled-components';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { spacing, colors, zIndex, easing, fadeIn } from 'ui/theme';
import { Delete } from 'ui/icons/action/Delete';
import { pxToRem } from 'utils/convert';
import { useTranslations } from '../../../hooks/useTranslations';
import { useDeleteImageOverlayContext } from '../../../contexts/DeleteImageOverlayContext';
import { useUploadAssetsContext } from 'modules/creativeLibrary/contexts/UploadAssetsContext';

import { useAssetsQueryContext } from 'modules/creativeLibrary/contexts/AssetsQueryContext';
import { TakeoverIntent } from 'modules/creativeLibrary/types';
import { useTakeoverContext } from 'modules/creativeLibrary/contexts/Takeover';
import { useDeleteAssetContentsAndFolders } from 'modules/creativeLibrary/hooks/useDeleteAssetContentsAndFolders';
import { SelectionTypes } from 'utils/contexts/selection';

const DeleteImageOverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${colors.backgroundScreen};
  justify-content: space-between;
  z-index: ${zIndex.modalOverlay};
  animation-duration: 200ms;
  animation-fill-mode: forwards;
  animation-name: ${fadeIn};
  animation-timing-function: ${easing.entrance};
`;

const BottomActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.half};
  background: ${colors.backgroundPrimary};
  border-top: 1px solid ${colors.borderPrimary};
  padding: ${spacing.plusHalf};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
`;

const ActionText = styled.div`
  color: ${colors.textPrimary};
  font-size: ${pxToRem(14)};
  font-weight: 400;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const DeleteImageOverlay = () => {
  const { t } = useTranslations();
  const { imgsIdsToDelete, setImgsIdsToDelete } = useDeleteImageOverlayContext();
  const { pendingImages, removeSelectedUploads } = useUploadAssetsContext();
  const { refetch } = useAssetsQueryContext();
  const { deleteItems, isLoading } = useDeleteAssetContentsAndFolders();
  const imgsToDeleteCount = imgsIdsToDelete.length;
  const { currentIntent } = useTakeoverContext();

  if (!imgsToDeleteCount) return null;

  const assetsToDelete = pendingImages.filter(
    (img) => imgsIdsToDelete.includes(img.id) && img.asset?.id,
  );

  const handleDelete = async () => {
    if (assetsToDelete.length > 0) {
      await deleteItems({
        itemsToDelete: assetsToDelete.map((c) => ({
          id: c.asset!.id,
          name: c.selected.name,
          type: SelectionTypes.Content,
        })),
      });
    }

    removeSelectedUploads(imgsIdsToDelete);
    setImgsIdsToDelete([]);
    refetch();
  };

  const getDeleteButtonText = () => {
    return imgsToDeleteCount > 1
      ? t('caption.confirmDeleteImages', { count: imgsToDeleteCount })
      : t('caption.confirmDeleteImage');
  };

  return (
    <DeleteImageOverlayContainer>
      <BottomActionBar>
        <ActionText>
          <strong>{getDeleteButtonText()}?</strong>{' '}
          <span>
            {currentIntent === TakeoverIntent.UPLOAD
              ? t(imgsToDeleteCount > 1 ? 'caption.imagesNotUploaded' : 'caption.imageNotUploaded')
              : t('caption.cannotUndo')}
          </span>
        </ActionText>
        <ActionButtons>
          <Button
            onClick={() => {
              setImgsIdsToDelete([]);
            }}
            isDisabled={isLoading}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            {t('button.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.DESTRUCTIVE}
            isLoading={isLoading}
          >
            <Delete />
            {getDeleteButtonText()}
          </Button>
        </ActionButtons>
      </BottomActionBar>
    </DeleteImageOverlayContainer>
  );
};
